import { axiosInstance } from "@/lib/axios-instance";

export interface Subscription {
  id: string;
  status: string;
  // Canonical plan tier from the org subscription (Stripe price metadata.tier).
  // Optional so responses predating this field still type-check.
  plan_tier?: "plus" | "pro" | "elite";
  product_key: string;
  current_at_period_end: boolean;
  cancel_at: number | null;
  canceled_at: number | null;
  items: Array<{
    id: string;
    price_id: string;
    product_id: string;
    quantity: number;
    nickname: string;
    deal_limit: number;
    amount: number;
    currency: string;
    current_period_end: number;
    current_period_start: number;
    recurring: {
      interval: string;
      interval_count: number;
    };
  }>;
  trial_end: number;
  trial_start: number;
  trial_settings: any;
  is_canceled: boolean;
  is_trialing: boolean;
}

export interface SyncSubscription {
  subscription_id: string;
  subscription_status: string;
  product_name: string;
  deal_limit: number;
  price_id: string;
}

export interface CheckoutSession {
  checkout_url: string;
  session_id: string;
}

export interface AvailableSubscription {
  key: string;
  price_id: string;
  amount: number;
  currency: string;
  interval: string;
  interval_count: number;
  product: {
    id: string;
    name: string;
    description: string;
    active: boolean;
    metadata: Record<string, any>;
  };
}

export interface Invoice {
  id: string;
  number: string;
  status: string;
  amount_paid: number;
  amount_due: number;
  currency: string;
  created: number;
  period_start: number;
  period_end: number;
  paid: boolean;
  invoice_pdf: string;
  hosted_invoice_url: string;
  lines: Array<{
    description: string;
    amount: number;
    currency: string;
    period: {
      start: number;
      end: number;
    };
  }>;
}

export interface InvoicesResponse {
  invoices: Invoice[];
  has_more: boolean;
  last_invoice_id: string | null;
}

class BillingAPI {
  async getCurrentSubscription(
    workspaceId: string
  ): Promise<Subscription | null> {
    const response = await axiosInstance.get("/billing/subscription", {
      params: {
        opportunity_id: workspaceId,
      },
    });
    return response.data.result;
  }

  async createCheckoutSession(
    workspaceId: string,
    productKey: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<CheckoutSession> {
    const response = await axiosInstance.post("/billing/checkout-session", {
      opportunity_id: workspaceId,
      product_key: productKey,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });
    return response.data.result;
  }

  async refreshLocalSubscription(
    workspaceId: string,
    subscriptionId: string,
    newPriceId: string
  ): Promise<SyncSubscription> {
    const response = await axiosInstance.post("/billing/subscription/sync", {
      opportunity_id: workspaceId,
      subscription_id: subscriptionId,
      new_price_id: newPriceId,
    });
    return response.data;
  }

  async cancelSubscription(
    workspaceId: string,
    subscriptionId: string
  ): Promise<Subscription> {
    const response = await axiosInstance.post("/billing/subscription/cancel", {
      opportunity_id: workspaceId,
      subscription_id: subscriptionId,
    });
    return response.data;
  }

  async getAvailableSubscriptions(): Promise<AvailableSubscription[]> {
    const response = await axiosInstance.get(
      "/billing/available-subscriptions"
    );
    return response.data.result || [];
  }

  async getSubscriptionInvoices(
    workspaceId: string,
    startingAfter?: string
  ): Promise<InvoicesResponse> {
    const response = await axiosInstance.get("/billing/subscription/invoices", {
      params: {
        opportunity_id: workspaceId,
        ...(startingAfter && { starting_after: startingAfter }),
      },
    });
    return response.data.result;
  }
}

export default new BillingAPI();
